import { NodeConnection, WorkflowNode } from "../types/workflow";

function getAvailableNodesForVariables(
    selectedNode: WorkflowNode,
    allNodes: WorkflowNode[],
    nodeConnections: NodeConnection[]
): WorkflowNode[] {
    if (!selectedNode?.node?.id) {
        return [];
    }

    // Build graph adjacency lists
    const parentMap = new Map<string, string[]>(); // nodeId -> parent nodeIds
    const childMap = new Map<string, string[]>(); // nodeId -> child nodeIds
    const allNodesMap = new Map<string, WorkflowNode>();

    allNodes.forEach(node => {
        if (node?.node?.id) {
            allNodesMap.set(node.node.id, node);
        }
    });

    nodeConnections.forEach(conn => {
        const startId = conn.start_node.id;
        const endId = conn.end_node.id;
        
        if (!parentMap.has(endId)) {
            parentMap.set(endId, []);
        }
        parentMap.get(endId)!.push(startId);
        
        if (!childMap.has(startId)) {
            childMap.set(startId, []);
        }
        childMap.get(startId)!.push(endId);
    });

    // Find trigger node (node with no incoming connections)
    const triggerNodeId = allNodes.find(node => 
        node.node.id && !parentMap.has(node.node.id)
    )?.node?.id;

    // Calculate distances from trigger using BFS
    const distances = new Map<string, number>();
    if (triggerNodeId) {
        const queue: Array<{ nodeId: string; distance: number }> = [{ nodeId: triggerNodeId, distance: 0 }];
        distances.set(triggerNodeId, 0);

        while (queue.length > 0) {
            const { nodeId, distance } = queue.shift()!;
            const children = childMap.get(nodeId) || [];
            
            children.forEach(childId => {
                if (!distances.has(childId)) {
                    distances.set(childId, distance + 1);
                    queue.push({ nodeId: childId, distance: distance + 1 });
                }
            });
        }
    }

    // Collect all ancestor nodes using BFS backwards from selected node
    const availableNodeIds = new Set<string>();
    const queue = [selectedNode.node.id];
    const visited = new Set<string>();

    while (queue.length > 0) {
        const nodeId = queue.shift()!;
        if (visited.has(nodeId)) continue;
        visited.add(nodeId);

        const parents = parentMap.get(nodeId) || [];
        parents.forEach(parentId => {
            if (!visited.has(parentId)) {
                availableNodeIds.add(parentId);
                queue.push(parentId);
            }
        });
    }

    // Convert to WorkflowNode array and sort by distance (furthest first)
    const availableNodes = Array.from(availableNodeIds)
        .map(id => allNodesMap.get(id))
        .filter((node): node is WorkflowNode => node !== undefined)
        .sort((a, b) => {
            const distanceA = distances.get(a.node.id || '') ?? Infinity;
            const distanceB = distances.get(b.node.id || '') ?? Infinity;
            
            if (distanceA !== distanceB) {
                return distanceA - distanceB; // Furthest (smaller distance) first
            }
            
            // If same distance, sort by created_time (oldest first)
            const timeA = new Date(a.node.created_time || '').getTime();
            const timeB = new Date(b.node.created_time || '').getTime();
            return timeA - timeB;
        });

    return availableNodes;
} 

export default getAvailableNodesForVariables;